import { zodResolver } from "@hookform/resolvers/zod";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
} from "@hortis/ui/breadcrumb";
import { Button, IconButton } from "@hortis/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@hortis/ui/dialog";
import styles from "@hortis/ui/dialog/dialog.module.css";
import { UploadCloud2, XClose } from "@hortis/ui/icons";
import { toast } from "@hortis/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { namedOperations } from "generated/graphql";
import { ReactNode, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { queryClient } from "src/app";
import { FeaturedIcon } from "src/components/featured-icon/featured-icon";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import {
  AddMaterialPhotosButton,
  StagedImage,
} from "src/features/create-accession/components/accession-data-form/add-material-photos-button";
import { DiscardDialog } from "src/features/create-accession/components/accession-data-form/discard-dialog";
import { MaterialPhotos } from "src/features/create-accession/components/accession-data-form/material-photos";
import { UploadsInProgressDialog } from "src/features/create-accession/components/accession-data-form/uploads-in-progress-dialog";
import { MaterialImageSchema } from "src/features/create-accession/components/add-plant-material-form/material-image-schema";
import { convertDatesToISO } from "src/utils/convert-dates-to-iso";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { createMaterialImagesDocument } from "./graphql";

const schema = z.object({
  images: z.array(MaterialImageSchema).nullish(),
});

export const CreatePhotosDialog = ({
  open: openControlled,
  onOpenChange: onOpenChangeControlled,
  materialId,
  children,
  onSuccess,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  materialId: string;
  children?: ReactNode;
  onSuccess?: () => void;
}) => {
  const [openInternal, onOpenChangeInternal] = useState(
    openControlled ?? false,
  );
  const open = openControlled ?? openInternal;
  const onOpenChange = onOpenChangeControlled ?? onOpenChangeInternal;
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [stagedPhotos, setStagedPhotos] = useState<Array<StagedImage>>([]);
  const [uploadsInProgressDialogOpen, setUploadsInProgressDialogOpen] =
    useState(false);

  const form = useForm<z.infer<typeof schema>>({
    criteriaMode: "all",
    resolver: zodResolver(schema),
  });

  const createObservation = useGqlMutation({
    mutation: createMaterialImagesDocument,
    onError: () => {
      toast.error("Failed to add photos");
    },
    onSuccess: (res) => {
      if (!res.createMaterialImages.success) {
        toast.error("Failed to add observation");
      }
      toast.success(
        `${res.createMaterialImages.materialImages.length} photo${res.createMaterialImages.materialImages.length === 1 ? "" : "s"} added`,
      );
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetMaterial],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      onOpenChange(false);
      onSuccess?.();
      setStagedPhotos([]);
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (
      (Object.keys(form.formState.dirtyFields).length !== 0 ||
        stagedPhotos.some((i) => !i.complete)) &&
      !discardDialogOpen
    ) {
      setDiscardDialogOpen(true);
      return;
    }
    onOpenChange(open);
  };

  const handleSubmit = async () => {
    if (!uploadsInProgressDialogOpen && stagedPhotos.some((i) => !i.complete)) {
      setUploadsInProgressDialogOpen(true);
      return;
    }

    if (materialId == null) {
      toast.error("Failed to upload photos");
      throw new Error("Material ID is required");
    }

    await createObservation.mutateAsync({
      materialId,
      input: stagedPhotos
        .filter((i): i is typeof i & { assetUrl: string } => i.assetUrl != null)
        .filter((i) => i.complete)
        .map(({ assetUrl, description, capturedAt, capturedBy }) => ({
          assetUrl,
          description,
          capturedBy,
          capturedAt,
        }))
        .map(convertDatesToISO),
    });
  };

  useEffect(() => {
    if (open) {
      setStagedPhotos([]);
    }
    setDiscardDialogOpen(false);
  }, [open, form]);

  const hasPhotos = stagedPhotos.length > 0;

  const content = (
    <FormProvider {...form}>
      <UploadsInProgressDialog
        open={uploadsInProgressDialogOpen}
        onOpenChange={setUploadsInProgressDialogOpen}
        onDiscard={() => {
          form.handleSubmit(handleSubmit)();
        }}
      />
      <form>
        <div className="space-y-4 px-5 pb-4 pt-5">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>Upload photos</BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="absolute right-5 top-0 gap-1 flex items-center">
            <Tooltip>
              <DiscardDialog
                onDiscard={() => {
                  handleOpenChange(false);
                }}
                open={discardDialogOpen}
                onOpenChange={setDiscardDialogOpen}
              >
                <TooltipTrigger asChild>
                  <IconButton
                    ariaLabel="Close"
                    variant="tertiaryGray"
                    testId="close-create-photos-dialog"
                    className="p-1.5"
                    size="sm"
                    icon={XClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenChange(false);
                    }}
                    onFocusCapture={stopEventPropagation}
                  />
                </TooltipTrigger>
              </DiscardDialog>
              <TooltipContent side="bottom" sideOffset={0}>
                Discard photos
              </TooltipContent>
            </Tooltip>
          </div>
          <div className={twMerge("min-h-24 flex flex-row gap-2 items-start")}>
            <MaterialPhotos
              images={stagedPhotos}
              deletePhoto={(id) => {
                setStagedPhotos((images) => images.filter((i) => i.id !== id));
              }}
              onPhotoSave={(id, newPhoto) => {
                setStagedPhotos((images) =>
                  images.map((image) => (image.id === id ? newPhoto : image)),
                );
              }}
            />
            <Tooltip>
              <AddMaterialPhotosButton
                images={stagedPhotos}
                setImages={setStagedPhotos}
                className={twMerge(!hasPhotos && "w-full")}
              >
                <TooltipTrigger asChild>
                  <div className="group hover:border-primary-200 hover:bg-primary-25 transition flex cursor-pointer text-center items-center flex-col gap-3 py-5 px-7 border border-grey-300 text-grey-500 text-sm border-dashed rounded-xl">
                    {hasPhotos ? (
                      <UploadCloud2 className="h-4 w-4 group-hover:text-primary-700 transition" />
                    ) : (
                      <FeaturedIcon
                        color="gray"
                        className="group-hover:bg-primary-100 group-hover:border-primary-50 group-hover:ring-primary-50 transition"
                      >
                        <UploadCloud2 />
                      </FeaturedIcon>
                    )}
                    {!hasPhotos && (
                      <div className="space-y-1">
                        <div>
                          <span className="text-primary-700 font-medium">
                            Click to upload
                          </span>{" "}
                          or drag and drop
                        </div>
                        <div>SVG, PNG, JPG or GIF (max. 20MB)</div>
                      </div>
                    )}
                  </div>
                </TooltipTrigger>
              </AddMaterialPhotosButton>
              <TooltipContent side="bottom">Upload photos</TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-grey-100 py-3 pl-4 pr-5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="primary"
                type="submit"
                size="sm"
                loading={form.formState.isSubmitting}
                testId="create-photos-button"
                onFocusCapture={stopEventPropagation}
                disabled={!open}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  form.handleSubmit(handleSubmit)(e);
                }}
              >
                Add photos
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={0}>
              Add photos
            </TooltipContent>
          </Tooltip>
        </div>
      </form>
    </FormProvider>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children}
      <DialogContent
        className={twMerge(
          "top-12 flex max-h-[calc(100%-96px)] w-full max-w-[calc(100%-32px)] md:max-w-[550px] translate-y-0 flex-col gap-0! rounded-xl bg-white p-0! [@media(min-height:953px)]:top-32 [@media(min-height:953px)]:max-h-[calc(100%-256px)]",
          styles.dialogNoY,
        )}
        data-cy="create-photos-dialog"
      >
        <DialogTitle className="sr-only">Add photos</DialogTitle>
        <DialogDescription className="sr-only">
          Add photos form
        </DialogDescription>
        {content}
      </DialogContent>
    </Dialog>
  );
};
